import type { RequiredAndNonNullable } from "@sinch/types/utility.types";

import {
  add,
  both,
  either,
  equals,
  includes,
  is,
  isEmpty,
  isNil,
  map,
  mapObjIndexed,
  mergeWith,
  omit,
  pipe,
  reduce,
  reject,
  sort,
  unless,
  when,
} from "ramda";
import { isArray, isNotNil } from "ramda-adjunct";

/**
 * Recursively remove empty values from object
 */
export function cleanNested<T>(obj: T): T {
  return pipe(
    map(
      when(
        both(is(Object), (item) => !(item instanceof Date)),
        cleanNested
      )
    ),
    reject(either(isNil, isEmpty))
  )(obj as T[]) as T; // Type definition for ramda is not defined for objects
}

/**
 * Recursively remove empty values from object, but preserve empty arrays and empty strings.
 * This is useful when you want to explicitly clear fields in multi-edit forms.
 * Removes: null, undefined, empty objects ({})
 * Keeps: empty arrays ([]), empty strings ("")
 */
export function cleanNestedPreserveArrays<T>(obj: T): T {
  return pipe(
    map(
      when(
        both(is(Object), (item) => !(item instanceof Date)),
        cleanNestedPreserveArrays
      )
    ),
    // Reject nil values and empty objects (but keep empty arrays and empty strings)
    reject(
      either(
        isNil,
        both(both(is(Object), isEmpty), (item) => !isArray(item))
      )
    )
  )(obj as T[]) as T; // Type definition for ramda is not defined for objects
}

/**
 * Recursively remove empty values from object.
 * For arrays: keeps null values
 * For objects: removes null values
 * Removes: undefined, empty strings, empty arrays, empty objects, null (in objects only)
 */
export function cleanNestedKeepNullForArrays<T>(obj: T): T {
  const isAnArray = isArray(obj);

  return pipe(
    map(
      when(
        both(is(Object), (item) => !(item instanceof Date)),
        cleanNestedKeepNullForArrays
      )
    ),
    // For arrays: keep null, reject undefined and empty values
    // For objects: reject null, undefined and empty values
    reject(
      isAnArray
        ? either(
            equals(undefined),
            both((value) => value !== null, isEmpty)
          )
        : either(isNil, isEmpty)
    )
  )(obj as T[]) as T; // Type definition for ramda is not defined for objects
}

export const hasTruthyProps =
  <Prop extends keyof any>(props: Prop[]) =>
  <ObjectIn extends { [K in Prop]?: any }>(o: ObjectIn): o is ObjectIn & RequiredAndNonNullable<Pick<ObjectIn, Prop>> =>
    props.every((prop) => !!o[prop as keyof ObjectIn]);

/**
 * Calculates the sum of numeric properties of an object.
 *
 * @example sumByProps<{ a: number, b: number }>()([{ a: 1, b: 2 }, { a: 3, b: 4 }]) // { a: 4, b: 6 }
 */
export const sumByProps = <TObj extends Record<string, number> = Record<string, number>>(list: TObj[]): TObj =>
  reduce<TObj, TObj>(mergeWith(add), {} as TObj)(list);

export const hasNotProp =
  <T,>(excludedList: (keyof T | string)[]): ((propName: keyof T | string) => boolean) =>
  (propName) =>
    !includes(propName, excludedList);

/**
 * If first argument is true, return second argument, otherwise return empty type of array or object base on first argument
 */
export function includeIf<T extends Record<string, any> | any[]>(
  cond: boolean,
  val: T,
  def?: never
): T | (T extends any[] ? [] : Record<string, any>);
/**
 * If first argument is true, return second argument, otherwise return third argument
 */
export function includeIf<
  T extends Record<string, any> | any[],
  D extends T extends any[] ? any[] : Record<string, any>,
>(cond: boolean, val: T, def?: D): T | D;
export function includeIf<
  T extends Record<string, any> | any[],
  D extends T extends any[] ? any[] : Record<string, any>,
>(cond: boolean, val: T, def?: D): T | D | Record<string, any> | [] {
  if (cond) {
    return val;
  } else if (isNotNil(def)) {
    return def;
  }
  return isArray(val) ? [] : {};
}

type DeepRemovePropertyFn = <TProp extends string, TObj extends Record<string, unknown> | Record<string, unknown>[]>(
  property: TProp,
  arg: TObj
) => TObj extends Record<string, unknown>[] ? TObj : Omit<TObj, TProp>;

const omitFromObject = (property: string) => unless(is(Array), omit([property]));

export const deepRemoveProperty: DeepRemovePropertyFn = (property, object) =>
  when(
    either(is(Array), is(Object)),
    pipe(
      // @ts-ignore complex type definition
      omitFromObject(property),
      map((a: Record<string, unknown>) => deepRemoveProperty(property, a))
    )
  )(object);

/**
 * Recursively traverses a JSON structure.
 * If it finds an array, it sorts the array based on the stringified value of its items.
 */
const deepSortArrays = (data: unknown): unknown => {
  if (Array.isArray(data)) {
    // 1. Recursively map over the array to handle nested structures first
    const processedItems = data.map(deepSortArrays);

    // 2. Sort the current array using a stable string comparison
    // This ensures [1, 2] becomes [1, 2] and [2, 1] becomes [1, 2]
    return sort((a, b) => {
      const itemA =
        is(Object, data) && data !== null ? JSON.stringify(a, Object.keys(a as object).sort()) : JSON.stringify(a);
      const itemB =
        is(Object, data) && data !== null ? JSON.stringify(b, Object.keys(b as object).sort()) : JSON.stringify(b);
      return itemA.localeCompare(itemB);
    }, processedItems);
  }

  // Check if it's a plain object (and not null)
  if (is(Object, data) && data !== null) {
    // Recursively process the values of the object
    // Note: We don't need to sort Keys, because Ramda's equals() already handles unordered object keys.
    return mapObjIndexed(deepSortArrays, data as Record<string, unknown>);
  }

  return data;
};

/**
 * Checks if two JSON objects are equal, ignoring the order of items in any arrays.
 *
 * Example:
 * areJsonsEqual({ a: [1, 2] }, { a: [2, 1] }) // returns true
 */
export const sortIndependentEquals = (a: unknown, b: unknown): boolean => equals(deepSortArrays(a), deepSortArrays(b));
